import { useCallback, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { nodeTypes } from './nodes';
import { applyDagreLayout, getNodeColor } from './utils/layoutConfig';

/**
 * React Flow canvas with custom nodes and automatic layout
 */
export function GraphCanvas({
  nodes: inputNodes,
  edges: inputEdges,
  vizType,
  archMode = 'structure',
  colorMode = 'complexity',
  searchQuery = '',
  onNodeClick,
  onNodeDoubleClick,
  onEdgeClick,
  selectedEdgeId
}) {
  // Inject colorMode, archMode, and search match status into each node's data
  const nodesWithExtras = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return inputNodes.map(node => {
      const label = (node.data.label || '').toLowerCase();
      const path = (node.data.path || node.data.relativePath || '').toLowerCase();
      const isMatch = !query || label.includes(query) || path.includes(query);

      return {
        ...node,
        data: {
          ...node.data,
          colorMode,
          archMode,
          searchMatch: query ? (isMatch ? 'match' : 'dimmed') : null
        }
      };
    });
  }, [inputNodes, colorMode, archMode, searchQuery]);

  // Apply layout to position nodes
  const layoutedData = useMemo(() => {
    return applyDagreLayout(nodesWithExtras, inputEdges, vizType);
  }, [nodesWithExtras, inputEdges, vizType]);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedData.edges);

  // Update when input changes
  useEffect(() => {
    const { nodes: ln, edges: le } = applyDagreLayout(nodesWithExtras, inputEdges, vizType);
    setNodes(ln);
    setEdges(le);
  }, [nodesWithExtras, inputEdges, vizType, setNodes, setEdges]);

  // Handle node click
  const handleNodeClick = useCallback((event, node) => {
    if (!onNodeClick) return;

    onNodeClick({
      type: node.data.nodeType,
      id: node.data.symbolId || node.id,
      path: node.data.path || node.data.relativePath,
      name: node.data.label,
      fileName: node.data.fileName,
    });
  }, [onNodeClick]);

  // Handle node double-click (drill-down)
  const handleNodeDoubleClick = useCallback((event, node) => {
    if (!onNodeDoubleClick) return;

    onNodeDoubleClick({
      type: node.data.nodeType,
      id: node.id,
      path: node.data.path || node.data.relativePath,
      name: node.data.label,
      hasChildren: node.data.hasChildren,
    });
  }, [onNodeDoubleClick]);

  // Handle edge click
  const handleEdgeClick = useCallback((event, edge) => {
    if (!onEdgeClick) return;

    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    onEdgeClick({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceNode,
      targetNode,
      data: edge.data || {},
      style: edge.style,
    });
  }, [onEdgeClick, nodes]);

  // Mark selected edge and cross-boundary edges with different styling
  const edgesWithSelection = useMemo(() => {
    return edges.map(edge => {
      const classes = [];

      // Cross-boundary highlighting (when Boundaries filter is active)
      if (edge.data?.crossBoundaryHighlight) {
        classes.push('cross-boundary');
      }

      // Selected edge styling
      if (edge.id === selectedEdgeId) {
        classes.push('selected-edge');
      }

      if (classes.length === 0 && !selectedEdgeId) {
        return edge;
      }

      return {
        ...edge,
        className: classes.join(' ') || undefined,
        ...(edge.id === selectedEdgeId ? {
          style: {
            ...edge.style,
            stroke: edge.data?.crossBoundaryHighlight ? '#f97316' : '#3b82f6',
            strokeWidth: 4,
          }
        } : {})
      };
    });
  }, [edges, selectedEdgeId]);

  // MiniMap node color
  const minimapNodeColor = useCallback((node) => {
    return getNodeColor(node.data?.nodeType, node.data?.kind);
  }, []);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edgesWithSelection}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={handleNodeClick}
      onNodeDoubleClick={handleNodeDoubleClick}
      onEdgeClick={handleEdgeClick}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.1}
      maxZoom={2}
      defaultEdgeOptions={{
        type: 'smoothstep',
        interactionWidth: 20,
      }}
      elementsSelectable={true}
      edgesFocusable={true}
      edgesReconnectable={false}
      selectNodesOnDrag={false}
    >
      <Background variant="dots" gap={16} size={1} color="rgba(255,255,255,0.04)" />
      <Controls showInteractive={false} />
      <MiniMap
        nodeColor={minimapNodeColor}
        maskColor="rgba(0,0,0,0.4)"
        style={{
          backgroundColor: '#0d0618',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '8px',
        }}
      />
    </ReactFlow>
  );
}

export default GraphCanvas;
